import React, { useState } from "react";
import Navigation from "../../components/Navigation/Navigation";
import { Row, Breadcrumb, Tab, Tabs, Card, Table, Badge, Container } from "react-bootstrap";
import Loader from "../../components/Common/Loader";
import * as Icon from "react-feather";
import "./Manual.css";

const Manual = () => {
  const [sideMenu, setSideMenu] = useState(true);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("general");

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const _onSideMenu = (active) => {
    setSideMenu(active);
  };

  const ScreenshotPlaceholder = ({ title, description }) => (
    <div className="screenshot-placeholder">
      <Icon.Image size={48} className="placeholder-icon" />
      <p className="placeholder-title">{title}</p>
      <p className="placeholder-desc">{description}</p>
    </div>
  );

  return (
    <div className="page-wrapper">
      <Navigation onClick={_onSideMenu} />

      <div className={`main-content d-flex flex-column ${sideMenu ? "" : "hide-sidemenu"}`}>
        {loading && <Loader message="Cargando manual..." />}

        <div className="main-content-header">
          <Breadcrumb>
            <h1>📚 Manual del Sistema</h1>
          </Breadcrumb>
        </div>

        <Row>
          <div className="col-12">
            <Card className="manual-card">
              <Card.Body>
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="manual-tabs mb-4"
                >
                  {/* TAB 1: Guía General */}
                  <Tab eventKey="general" title={<span><Icon.Book size={16} className="me-2"/>Guía General</span>}>
                    <div className="manual-content">
                      <h2 className="section-title">📘 Guía del Usuario - Sistema Kinexion CRM</h2>
                      <p className="lead">Sistema de Gestión de Transporte</p>

                      <hr />

                      <h3>Introducción</h3>
                      <p>
                        Bienvenido a <strong>Kinexion CRM</strong>, el sistema integral de gestión de transporte
                        diseñado para optimizar las operaciones de tu flota, administrar viajes, controlar
                        finanzas y mantener una comunicación efectiva con clientes.
                      </p>

                      <h4>Características Principales</h4>
                      <ul className="feature-list">
                        <li><Icon.Truck size={18}/> <strong>Gestión de Flotilla</strong>: Control completo de unidades vehiculares</li>
                        <li><Icon.Calendar size={18}/> <strong>Calendario de Operaciones</strong>: Visualización y programación de viajes</li>
                        <li><Icon.Navigation size={18}/> <strong>Gestión de Rutas</strong>: Planificación y monitoreo de rutas</li>
                        <li><Icon.DollarSign size={18}/> <strong>Control Financiero</strong>: Pagos, nómina, combustible y peajes</li>
                        <li><Icon.Tool size={18}/> <strong>Mantenimiento</strong>: Registro de mantenimientos, fumigación y limpieza</li>
                        <li><Icon.Users size={18}/> <strong>Gestión de Clientes</strong>: Portal para solicitudes de viaje</li>
                      </ul>

                      <ScreenshotPlaceholder
                        title="Pantalla de Inicio"
                        description="Captura de la pantalla principal del sistema mostrando el dashboard"
                      />

                      <hr />

                      <h3>Inicio de Sesión</h3>

                      <h4>Acceso al Sistema</h4>
                      <ol>
                        <li>Abre tu navegador web y accede a la URL del sistema Kinexion</li>
                        <li>En la página de inicio, haz clic en <strong>"Iniciar Sesión"</strong></li>
                        <li>Ingresa tu correo electrónico y contraseña</li>
                        <li>Haz clic en <strong>"Entrar"</strong></li>
                      </ol>

                      <ScreenshotPlaceholder
                        title="Pantalla de Login"
                        description="Formulario de inicio de sesión"
                      />

                      <h4>Registro de Nueva Cuenta</h4>
                      <ol>
                        <li>Desde la página principal, haz clic en <strong>"Registrarse"</strong></li>
                        <li>Completa el formulario con los datos de tu empresa</li>
                        <li>Recibirás un correo de verificación</li>
                        <li>Haz clic en el enlace de verificación para activar tu cuenta</li>
                      </ol>

                      <hr />

                      <h3>Navegación General</h3>

                      <h4>Barra Superior</h4>
                      <p>La barra de navegación superior contiene:</p>

                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Elemento</th>
                            <th>Descripción</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong>Logo Kinexion</strong></td>
                            <td>Haz clic para ir a la página principal</td>
                          </tr>
                          <tr>
                            <td><strong>Menú Hamburguesa (☰)</strong></td>
                            <td>Expande o colapsa el menú lateral</td>
                          </tr>
                          <tr>
                            <td><strong>Mensajes (📧)</strong></td>
                            <td>Notificaciones y correos internos</td>
                          </tr>
                          <tr>
                            <td><strong>Notificaciones (🔔)</strong></td>
                            <td>Alertas del sistema</td>
                          </tr>
                          <tr>
                            <td><strong>Perfil de Usuario</strong></td>
                            <td>Acceso a configuraciones y cierre de sesión</td>
                          </tr>
                        </tbody>
                      </Table>

                      <ScreenshotPlaceholder
                        title="Barra de Navegación"
                        description="Barra superior con todas las opciones de navegación"
                      />

                      <h4>Menú Lateral</h4>
                      <p>El menú lateral izquierdo proporciona acceso a todos los módulos del sistema:</p>

                      <div className="menu-structure">
                        <pre>{`
📁 Operaciones
   ├── Flotilla
   ├── Calendario
   ├── Programa de Viajes
   ├── Rutas
   ├── Monitoreo
   ├── Mantenimiento
   ├── Fumigación
   ├── Limpieza
   └── Solicitudes de Viaje

👥 RRHH
   ├── Usuarios
   ├── Empleados
   └── Nómina/Comisiones

🛒 Ventas
   ├── Clientes
   ├── Cuentas por Cobrar
   └── Viajes Pagados

💵 Finanzas
   ├── Disel y Rendimiento
   └── Cargas de Tag

📊 Uso y Facturación

👤 Cliente
   ├── Calendario
   └── Solicitudes de Viaje
                        `}</pre>
                      </div>

                      <ScreenshotPlaceholder
                        title="Menú Lateral"
                        description="Menú lateral expandido mostrando todas las opciones"
                      />

                      <hr />

                      <h3>Roles y Permisos</h3>
                      <p>El sistema cuenta con diferentes roles, cada uno con acceso específico a los módulos:</p>

                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Rol</th>
                            <th>Módulos Disponibles</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td><Badge bg="primary">Admin</Badge></td><td>Todos los módulos</td></tr>
                          <tr><td><Badge bg="info">Operador</Badge></td><td>Operaciones</td></tr>
                          <tr><td><Badge bg="info">Tráfico</Badge></td><td>Operaciones</td></tr>
                          <tr><td><Badge bg="info">Monitorista</Badge></td><td>Operaciones</td></tr>
                          <tr><td><Badge bg="secondary">Taller</Badge></td><td>Operaciones</td></tr>
                          <tr><td><Badge bg="warning">RRHH</Badge></td><td>RRHH</td></tr>
                          <tr><td><Badge bg="success">Vendedor</Badge></td><td>Ventas</td></tr>
                          <tr><td><Badge bg="dark">Compras</Badge></td><td>Finanzas</td></tr>
                          <tr><td><Badge bg="success">Finanzas</Badge></td><td>Finanzas, Facturación</td></tr>
                          <tr><td><Badge bg="success">Facturación</Badge></td><td>Finanzas, Facturación</td></tr>
                          <tr><td><Badge bg="light" text="dark">Cliente</Badge></td><td>Cliente</td></tr>
                        </tbody>
                      </Table>
                    </div>
                  </Tab>

                  {/* TAB 2: Operaciones */}
                  <Tab eventKey="operaciones" title={<span><Icon.Truck size={16} className="me-2"/>Operaciones</span>}>
                    <div className="manual-content">
                      <h2 className="section-title">🚚 Guía del Módulo de Operaciones</h2>

                      <hr />

                      <h3>Gestión de Flotilla</h3>
                      <p>
                        El módulo de Flotilla permite administrar todas las unidades vehiculares de tu empresa de transporte.
                        Aquí puedes registrar tractocamiones, tortones, remolques y cualquier tipo de unidad que forme parte de tu flota.
                      </p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Operaciones → Flotilla<br/>
                        <strong>URL:</strong> <code>/flotilla/</code>
                      </div>

                      <ScreenshotPlaceholder
                        title="Módulo de Flotilla"
                        description="Tabla de unidades vehiculares registradas"
                      />

                      <h4>Visualización de Unidades</h4>
                      <p>Al acceder al módulo, verás una tabla con todas las unidades registradas:</p>

                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Columna</th>
                            <th>Descripción</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td><strong>Nombre</strong></td><td>Identificador único de la unidad</td></tr>
                          <tr><td><strong>Tipo</strong></td><td>Categoría del vehículo (Tractocamión, Torton, Remolque, etc.)</td></tr>
                          <tr><td><strong>Marca y Modelo</strong></td><td>Fabricante y modelo del vehículo</td></tr>
                          <tr><td><strong>Ejes</strong></td><td>Número de ejes de la unidad</td></tr>
                          <tr><td><strong>Año</strong></td><td>Año de fabricación</td></tr>
                          <tr><td><strong>Placas</strong></td><td>Número de placas de circulación</td></tr>
                          <tr><td><strong>Acciones</strong></td><td>Botones para editar la unidad</td></tr>
                        </tbody>
                      </Table>

                      <h4>Agregar Nueva Unidad</h4>
                      <ol>
                        <li>Haz clic en el botón <strong>"Agregar Unidad"</strong> (color azul, esquina superior derecha)</li>
                        <li>Completa el formulario con la información requerida</li>
                        <li>Haz clic en <strong>"Guardar"</strong> para registrar la unidad</li>
                      </ol>

                      <ScreenshotPlaceholder
                        title="Formulario de Nueva Unidad"
                        description="Modal para agregar una nueva unidad vehicular"
                      />

                      <h4>Tipos de Unidades Disponibles</h4>
                      <ul>
                        <li><strong>Tractocamión</strong>: Camión de carga pesada con motor</li>
                        <li><strong>Torton</strong>: Camión mediano de carga</li>
                        <li><strong>Remolque</strong>: Caja sin motor para acoplarse al tractocamión</li>
                        <li><strong>Caja Seca</strong>: Contenedor cerrado para carga general</li>
                        <li><strong>Plataforma</strong>: Base plana para carga especial</li>
                        <li><strong>Refrigerado</strong>: Unidad con sistema de refrigeración</li>
                      </ul>

                      <hr />

                      <h3>Calendario de Operaciones</h3>
                      <p>
                        El calendario proporciona una vista visual de todos los viajes programados,
                        permitiendo una mejor planificación y distribución de recursos.
                      </p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Operaciones → Calendario<br/>
                        <strong>URL:</strong> <code>/calendar/</code>
                      </div>

                      <ScreenshotPlaceholder
                        title="Calendario de Operaciones"
                        description="Vista del calendario con viajes programados"
                      />

                      <h4>Código de Colores</h4>
                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Color</th>
                            <th>Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td><Badge bg="success">Verde</Badge></td><td>Viaje entregado/completado</td></tr>
                          <tr><td><Badge bg="warning">Amarillo</Badge></td><td>Viaje pendiente</td></tr>
                          <tr><td><Badge bg="info">Azul</Badge></td><td>Viaje en camino</td></tr>
                          <tr><td><Badge bg="danger">Rojo</Badge></td><td>Viaje cancelado</td></tr>
                        </tbody>
                      </Table>

                      <hr />

                      <h3>Programa de Viajes</h3>
                      <p>
                        Este es el módulo central para la gestión de viajes. Permite crear, editar,
                        monitorear y dar seguimiento a todos los viajes de la empresa.
                      </p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Operaciones → Programa de Viajes<br/>
                        <strong>URL:</strong> <code>/viajes/</code>
                      </div>

                      <ScreenshotPlaceholder
                        title="Programa de Viajes"
                        description="Tabla con todos los viajes registrados"
                      />

                      <h4>Estados de Viaje</h4>
                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Estado</th>
                            <th>Indicador</th>
                            <th>Significado</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>Entregado</td><td><Badge bg="success">Verde</Badge></td><td>Viaje completado exitosamente</td></tr>
                          <tr><td>Pendiente</td><td><Badge bg="warning">Amarillo</Badge></td><td>Viaje programado sin iniciar</td></tr>
                          <tr><td>En Camino</td><td><Badge bg="info">Azul</Badge></td><td>Viaje en progreso</td></tr>
                          <tr><td>Cancelado</td><td><Badge bg="danger">Rojo</Badge></td><td>Viaje cancelado</td></tr>
                        </tbody>
                      </Table>

                      <h4>Crear Nuevo Viaje</h4>
                      <ol>
                        <li>Haz clic en <strong>"Agregar Viaje"</strong></li>
                        <li>Completa la información del viaje (cliente, operador, unidad)</li>
                        <li>Define las fechas y paradas del viaje</li>
                        <li>Guarda el registro</li>
                      </ol>

                      <ScreenshotPlaceholder
                        title="Formulario de Nuevo Viaje"
                        description="Modal para crear un nuevo viaje"
                      />

                      <h4>Ver Detalles del Viaje</h4>
                      <ol>
                        <li>Localiza el viaje en la tabla</li>
                        <li>Haz clic en el ícono de ojo (👁️) en <strong>Acciones</strong></li>
                        <li>Se desplegará una sección con información detallada</li>
                      </ol>

                      <ScreenshotPlaceholder
                        title="Detalles del Viaje"
                        description="Vista expandida con información completa del viaje"
                      />

                      <hr />

                      <h3>Gestión de Rutas</h3>
                      <p>Módulo para planificar y visualizar las rutas más utilizadas.</p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Operaciones → Rutas<br/>
                        <strong>URL:</strong> <code>/rutas/</code>
                      </div>

                      <ScreenshotPlaceholder
                        title="Gestión de Rutas"
                        description="Pantalla de administración de rutas"
                      />

                      <hr />

                      <h3>Monitoreo</h3>
                      <p>Seguimiento en tiempo real de las operaciones activas.</p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Operaciones → Monitoreo<br/>
                        <strong>URL:</strong> <code>/operaciones/</code>
                      </div>

                      <ScreenshotPlaceholder
                        title="Monitoreo"
                        description="Panel de monitoreo de operaciones activas"
                      />

                      <hr />

                      <h3>Solicitudes de Viaje</h3>
                      <p>Gestión de las solicitudes de viaje enviadas por los clientes.</p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Operaciones → Solicitudes de Viaje<br/>
                        <strong>URL:</strong> <code>/gestionar-solicitudes/</code>
                      </div>

                      <h4>Estados de Solicitud</h4>
                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Estado</th>
                            <th>Descripción</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td><Badge bg="primary">Nueva</Badge></td><td>Solicitud recién recibida</td></tr>
                          <tr><td><Badge bg="warning">En Revisión</Badge></td><td>Siendo evaluada por el equipo</td></tr>
                          <tr><td><Badge bg="success">Aprobada</Badge></td><td>Aceptada y lista para programar</td></tr>
                          <tr><td><Badge bg="danger">Rechazada</Badge></td><td>No procede, con motivo</td></tr>
                          <tr><td><Badge bg="info">Programada</Badge></td><td>Convertida en viaje activo</td></tr>
                        </tbody>
                      </Table>

                      <ScreenshotPlaceholder
                        title="Solicitudes de Viaje"
                        description="Tabla de solicitudes pendientes de aprobar"
                      />
                    </div>
                  </Tab>

                  {/* TAB 3: Finanzas */}
                  <Tab eventKey="finanzas" title={<span><Icon.DollarSign size={16} className="me-2"/>Finanzas</span>}>
                    <div className="manual-content">
                      <h2 className="section-title">💵 Guía del Módulo de Finanzas</h2>

                      <hr />

                      <h3>RRHH y Nómina</h3>
                      <p>
                        Este módulo permite gestionar el personal de la empresa, incluyendo el control de usuarios
                        del sistema, registro de empleados y administración de la nómina y comisiones.
                      </p>

                      <h4>Gestión de Usuarios</h4>
                      <div className="info-box">
                        <strong>Acceso:</strong> RRHH → Usuarios<br/>
                        <strong>URL:</strong> <code>/usuarios/</code>
                      </div>

                      <ScreenshotPlaceholder
                        title="Gestión de Usuarios"
                        description="Tabla de usuarios del sistema"
                      />

                      <h4>Gestión de Empleados</h4>
                      <div className="info-box">
                        <strong>Acceso:</strong> RRHH → Empleados<br/>
                        <strong>URL:</strong> <code>/empleados/</code>
                      </div>

                      <ScreenshotPlaceholder
                        title="Gestión de Empleados"
                        description="Registro de empleados de la empresa"
                      />

                      <h4>Nómina y Comisiones</h4>
                      <div className="info-box">
                        <strong>Acceso:</strong> RRHH → Nómina/Comisiones<br/>
                        <strong>URL:</strong> <code>/finanzas/nomina/</code>
                      </div>

                      <ScreenshotPlaceholder
                        title="Nómina y Comisiones"
                        description="Cálculo de nómina y comisiones por viaje"
                      />

                      <hr />

                      <h3>Ventas y Cuentas por Cobrar</h3>

                      <h4>Gestión de Clientes</h4>
                      <div className="info-box">
                        <strong>Acceso:</strong> Ventas → Clientes<br/>
                        <strong>URL:</strong> <code>/clientes/</code>
                      </div>

                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Campo</th>
                            <th>Descripción</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>Razón Social</td><td>Nombre de la empresa</td></tr>
                          <tr><td>RFC</td><td>Registro Federal de Contribuyentes</td></tr>
                          <tr><td>Contacto</td><td>Persona de contacto</td></tr>
                          <tr><td>Teléfono</td><td>Número de contacto</td></tr>
                          <tr><td>Email</td><td>Correo electrónico</td></tr>
                          <tr><td>Dirección</td><td>Dirección fiscal</td></tr>
                          <tr><td>Condiciones de Pago</td><td>Días de crédito</td></tr>
                        </tbody>
                      </Table>

                      <ScreenshotPlaceholder
                        title="Gestión de Clientes"
                        description="Listado de clientes registrados"
                      />

                      <h4>Cuentas por Cobrar</h4>
                      <div className="info-box">
                        <strong>Acceso:</strong> Ventas → Cuentas por Cobrar<br/>
                        <strong>URL:</strong> <code>/finanzas/pagos-del-cliente/</code>
                      </div>

                      <h5>Estados de Pago</h5>
                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Estado</th>
                            <th>Indicador</th>
                            <th>Descripción</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>Al día</td><td><Badge bg="success">Verde</Badge></td><td>Dentro del plazo de pago</td></tr>
                          <tr><td>Próximo a vencer</td><td><Badge bg="warning">Amarillo</Badge></td><td>Vence en los próximos 5 días</td></tr>
                          <tr><td>Vencido</td><td><Badge bg="danger">Rojo</Badge></td><td>Pasada la fecha de vencimiento</td></tr>
                        </tbody>
                      </Table>

                      <ScreenshotPlaceholder
                        title="Cuentas por Cobrar"
                        description="Listado de facturas pendientes de pago"
                      />

                      <hr />

                      <h3>Diesel y Rendimiento</h3>
                      <p>Control del consumo de combustible de la flotilla.</p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Finanzas → Disel y Rendimiento<br/>
                        <strong>URL:</strong> <code>/finanzas/disel/</code>
                      </div>

                      <h4>Registro de Cargas de Combustible</h4>
                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Campo</th>
                            <th>Descripción</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>Unidad</td><td>Vehículo que recibe la carga</td></tr>
                          <tr><td>Operador</td><td>Conductor responsable</td></tr>
                          <tr><td>Fecha</td><td>Fecha de la carga</td></tr>
                          <tr><td>Estación</td><td>Gasolinera donde se cargó</td></tr>
                          <tr><td>Litros</td><td>Cantidad de combustible</td></tr>
                          <tr><td>Precio por Litro</td><td>Costo unitario</td></tr>
                          <tr><td>Total</td><td>Monto total pagado</td></tr>
                          <tr><td>Odómetro</td><td>Kilómetros al momento de la carga</td></tr>
                        </tbody>
                      </Table>

                      <ScreenshotPlaceholder
                        title="Control de Combustible"
                        description="Registro de cargas de diesel"
                      />

                      <h4>Cálculo de Rendimiento</h4>
                      <div className="formula-box">
                        <strong>Rendimiento = (Odómetro Actual - Odómetro Anterior) / Litros Cargados</strong>
                        <br/><br/>
                        <em>Ejemplo:</em><br/>
                        Odómetro anterior: 45,000 km<br/>
                        Odómetro actual: 45,500 km<br/>
                        Litros cargados: 200 L<br/>
                        <strong>Rendimiento: 2.5 km/L</strong>
                      </div>

                      <hr />

                      <h3>Cargas de Tag</h3>
                      <p>Gestión de las recargas de tags para pago de peajes en casetas.</p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Finanzas → Cargas de Tag<br/>
                        <strong>URL:</strong> <code>/finanzas/tag/</code>
                      </div>

                      <ScreenshotPlaceholder
                        title="Cargas de Tag"
                        description="Historial de recargas de tags para peajes"
                      />

                      <hr />

                      <h3>Uso y Facturación</h3>
                      <p>Visualización del consumo del sistema Kinexion y gestión de la facturación del servicio.</p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Uso y Facturación<br/>
                        <strong>URL:</strong> <code>/usage/</code>
                      </div>

                      <ScreenshotPlaceholder
                        title="Uso y Facturación"
                        description="Panel de uso del sistema y facturación"
                      />
                    </div>
                  </Tab>

                  {/* TAB 4: Mantenimiento */}
                  <Tab eventKey="mantenimiento" title={<span><Icon.Tool size={16} className="me-2"/>Mantenimiento</span>}>
                    <div className="manual-content">
                      <h2 className="section-title">🔧 Guía del Módulo de Mantenimiento</h2>

                      <hr />

                      <h3>Mantenimiento Vehicular</h3>
                      <p>
                        El módulo de mantenimiento permite registrar y dar seguimiento a todos los servicios
                        de mantenimiento preventivo y correctivo realizados a las unidades de la flotilla.
                      </p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Operaciones → Mantenimiento<br/>
                        <strong>URL:</strong> <code>/mantenimiento/mantenimiento/</code>
                      </div>

                      <h4>Tipos de Mantenimiento</h4>
                      <div className="row">
                        <div className="col-md-6">
                          <h5>Mantenimiento Preventivo</h5>
                          <ul>
                            <li>Cambio de aceite y filtros</li>
                            <li>Revisión de frenos</li>
                            <li>Alineación y balanceo</li>
                            <li>Inspección de llantas</li>
                            <li>Revisión de luces</li>
                            <li>Servicio de aire acondicionado</li>
                          </ul>
                        </div>
                        <div className="col-md-6">
                          <h5>Mantenimiento Correctivo</h5>
                          <ul>
                            <li>Reparación de motor</li>
                            <li>Cambio de embrague</li>
                            <li>Reparación de transmisión</li>
                            <li>Reparación de suspensión</li>
                            <li>Cambio de llantas</li>
                            <li>Reparaciones eléctricas</li>
                          </ul>
                        </div>
                      </div>

                      <ScreenshotPlaceholder
                        title="Registro de Mantenimiento"
                        description="Tabla con historial de mantenimientos"
                      />

                      <h4>Estados de Mantenimiento</h4>
                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Estado</th>
                            <th>Indicador</th>
                            <th>Significado</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>Programado</td><td><Badge bg="primary">Azul</Badge></td><td>Servicio pendiente de realizar</td></tr>
                          <tr><td>En Proceso</td><td><Badge bg="warning">Amarillo</Badge></td><td>Unidad actualmente en servicio</td></tr>
                          <tr><td>Completado</td><td><Badge bg="success">Verde</Badge></td><td>Servicio terminado</td></tr>
                          <tr><td>Cancelado</td><td><Badge bg="danger">Rojo</Badge></td><td>Servicio no realizado</td></tr>
                        </tbody>
                      </Table>

                      <h4>Cómo Registrar un Mantenimiento</h4>
                      <ol>
                        <li>Ve a <strong>Operaciones → Mantenimiento</strong></li>
                        <li>Haz clic en <strong>"Nuevo Registro"</strong></li>
                        <li>Selecciona la unidad</li>
                        <li>Elige el tipo de servicio</li>
                        <li>Completa los detalles (descripción, fecha, odómetro, costo)</li>
                        <li>Haz clic en <strong>"Guardar"</strong></li>
                      </ol>

                      <ScreenshotPlaceholder
                        title="Formulario de Mantenimiento"
                        description="Modal para registrar un nuevo mantenimiento"
                      />

                      <hr />

                      <h3>Fumigación</h3>
                      <p>
                        Control de los servicios de fumigación obligatorios para cajas, remolques y unidades
                        que transportan alimentos u otros productos que requieren certificación sanitaria.
                      </p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Operaciones → Fumigación<br/>
                        <strong>URL:</strong> <code>/mantenimiento/fumigacion/</code>
                      </div>

                      <div className="alert alert-warning">
                        <Icon.AlertTriangle size={20} className="me-2"/>
                        <strong>Importante:</strong> Según las normativas de transporte de alimentos,
                        la fumigación debe realizarse mínimo cada 30 días con empresas autorizadas por COFEPRIS.
                      </div>

                      <h4>Estados de Fumigación</h4>
                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Estado</th>
                            <th>Indicador</th>
                            <th>Significado</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>Vigente</td><td><Badge bg="success">Verde</Badge></td><td>Certificado válido</td></tr>
                          <tr><td>Por Vencer</td><td><Badge bg="warning">Amarillo</Badge></td><td>Vence en los próximos 7 días</td></tr>
                          <tr><td>Vencido</td><td><Badge bg="danger">Rojo</Badge></td><td>Certificado expirado</td></tr>
                        </tbody>
                      </Table>

                      <ScreenshotPlaceholder
                        title="Control de Fumigación"
                        description="Registro de certificados de fumigación"
                      />

                      <hr />

                      <h3>Limpieza</h3>
                      <p>
                        Registro de servicios de limpieza para mantener las unidades en condiciones óptimas
                        de higiene, especialmente importante para el transporte de alimentos.
                      </p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Operaciones → Limpieza<br/>
                        <strong>URL:</strong> <code>/mantenimiento/limpieza/</code>
                      </div>

                      <h4>Tipos de Limpieza</h4>
                      <ul>
                        <li><strong>Limpieza Básica</strong>: Lavado exterior, barrido interior, limpieza de cabina</li>
                        <li><strong>Limpieza Profunda</strong>: Lavado a presión interior, desinfección completa</li>
                        <li><strong>Limpieza Especializada</strong>: Para productos químicos o alimenticios</li>
                      </ul>

                      <h4>Frecuencia Recomendada</h4>
                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Tipo de Carga</th>
                            <th>Frecuencia de Limpieza</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>Alimentos</td><td>Después de cada viaje</td></tr>
                          <tr><td>Productos secos</td><td>Semanal</td></tr>
                          <tr><td>Químicos</td><td>Después de cada viaje</td></tr>
                          <tr><td>Carga general</td><td>Quincenal</td></tr>
                        </tbody>
                      </Table>

                      <ScreenshotPlaceholder
                        title="Control de Limpieza"
                        description="Registro de servicios de limpieza"
                      />

                      <hr />

                      <h3>Sistema de Alertas</h3>
                      <p>El sistema envía alertas automáticas para recordar servicios próximos:</p>

                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Tipo</th>
                            <th>Alerta</th>
                            <th>Condición</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>Mantenimiento</td><td><Badge bg="warning">Próximo</Badge></td><td>500 km antes del servicio</td></tr>
                          <tr><td>Mantenimiento</td><td><Badge bg="danger">Vencido</Badge></td><td>Kilómetros excedidos</td></tr>
                          <tr><td>Fumigación</td><td><Badge bg="warning">Próximo</Badge></td><td>7 días antes del vencimiento</td></tr>
                          <tr><td>Fumigación</td><td><Badge bg="danger">Vencido</Badge></td><td>Certificado expirado</td></tr>
                        </tbody>
                      </Table>
                    </div>
                  </Tab>

                  {/* TAB 5: Portal de Clientes */}
                  <Tab eventKey="clientes" title={<span><Icon.User size={16} className="me-2"/>Portal de Clientes</span>}>
                    <div className="manual-content">
                      <h2 className="section-title">👤 Guía del Portal de Clientes</h2>

                      <hr />

                      <h3>Introducción</h3>
                      <p>El Portal de Clientes de Kinexion está diseñado para que puedas:</p>
                      <ul className="feature-list">
                        <li><Icon.FileText size={18}/> Solicitar servicios de transporte de manera fácil</li>
                        <li><Icon.Calendar size={18}/> Visualizar tus viajes programados</li>
                        <li><Icon.Truck size={18}/> Dar seguimiento a tus envíos en tiempo real</li>
                        <li><Icon.BarChart2 size={18}/> Consultar el historial de tus servicios</li>
                      </ul>

                      <ScreenshotPlaceholder
                        title="Portal de Clientes"
                        description="Vista principal del portal para clientes"
                      />

                      <hr />

                      <h3>Acceso al Portal</h3>

                      <h4>Primer Acceso</h4>
                      <ol>
                        <li>Solicita credenciales a tu contacto en la empresa de transporte</li>
                        <li>Recibirás un correo con tu usuario y contraseña temporal</li>
                        <li>Accede a la URL del sistema</li>
                        <li>Cambia tu contraseña en el primer inicio de sesión</li>
                      </ol>

                      <ScreenshotPlaceholder
                        title="Login de Cliente"
                        description="Pantalla de inicio de sesión para clientes"
                      />

                      <hr />

                      <h3>Calendario de Cliente</h3>
                      <p>
                        El calendario te permite visualizar todos los viajes programados para tu empresa,
                        ayudándote a planificar tus operaciones de recepción y despacho.
                      </p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Cliente → Calendario<br/>
                        <strong>URL:</strong> <code>/calendario-cliente/</code>
                      </div>

                      <h4>Código de Colores</h4>
                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Color</th>
                            <th>Significado</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td><Badge bg="success">Verde</Badge></td><td>Viaje entregado/completado</td></tr>
                          <tr><td><Badge bg="warning">Amarillo</Badge></td><td>Viaje programado pendiente</td></tr>
                          <tr><td><Badge bg="info">Azul</Badge></td><td>Viaje en tránsito</td></tr>
                          <tr><td><Badge bg="danger">Rojo</Badge></td><td>Viaje cancelado</td></tr>
                        </tbody>
                      </Table>

                      <ScreenshotPlaceholder
                        title="Calendario del Cliente"
                        description="Vista del calendario con viajes programados para el cliente"
                      />

                      <hr />

                      <h3>Solicitudes de Viaje</h3>
                      <p>
                        Este módulo te permite enviar nuevas solicitudes de servicio de transporte directamente
                        a la empresa. El equipo de operaciones revisará tu solicitud y te confirmará disponibilidad.
                      </p>

                      <div className="info-box">
                        <strong>Acceso:</strong> Cliente → Solicitudes de Viaje<br/>
                        <strong>URL:</strong> <code>/solicitudes-viaje/</code>
                      </div>

                      <h4>Crear Nueva Solicitud</h4>
                      <ol>
                        <li>Ve a <strong>Cliente → Solicitudes de Viaje</strong></li>
                        <li>Haz clic en <strong>"Nueva Solicitud"</strong></li>
                        <li>Completa la información de la carga (tipo, descripción, peso)</li>
                        <li>Ingresa la dirección de origen y destino</li>
                        <li>Define las fechas y horarios deseados</li>
                        <li>Agrega requisitos especiales si es necesario</li>
                        <li>Haz clic en <strong>"Enviar Solicitud"</strong></li>
                      </ol>

                      <ScreenshotPlaceholder
                        title="Nueva Solicitud de Viaje"
                        description="Formulario para crear una solicitud de transporte"
                      />

                      <h4>Estados de Solicitud</h4>
                      <Table striped bordered hover className="manual-table">
                        <thead>
                          <tr>
                            <th>Estado</th>
                            <th>Indicador</th>
                            <th>Descripción</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>Nueva</td><td><Badge bg="primary">Azul</Badge></td><td>Solicitud recién enviada</td></tr>
                          <tr><td>En Revisión</td><td><Badge bg="warning">Amarillo</Badge></td><td>Siendo evaluada por el equipo</td></tr>
                          <tr><td>Aprobada</td><td><Badge bg="success">Verde</Badge></td><td>Aceptada, se programará el viaje</td></tr>
                          <tr><td>Rechazada</td><td><Badge bg="danger">Rojo</Badge></td><td>No procede (con explicación)</td></tr>
                          <tr><td>Programada</td><td><Badge bg="info">Verde oscuro</Badge></td><td>Viaje confirmado y calendarizado</td></tr>
                        </tbody>
                      </Table>

                      <ScreenshotPlaceholder
                        title="Lista de Solicitudes"
                        description="Tabla con mis solicitudes de viaje"
                      />

                      <hr />

                      <h3>Seguimiento de Envíos</h3>
                      <p>Una vez que tu solicitud se convierte en viaje programado, puedes dar seguimiento:</p>

                      <ul>
                        <li><strong>Estado General</strong>: Pendiente, En Camino, Entregado</li>
                        <li><strong>Ubicación Actual</strong>: Si hay integración GPS</li>
                        <li><strong>Hora Estimada de Llegada</strong>: Basada en la ruta</li>
                        <li><strong>Operador Asignado</strong>: Conductor responsable</li>
                        <li><strong>Unidad</strong>: Vehículo que realiza el traslado</li>
                      </ul>

                      <ScreenshotPlaceholder
                        title="Seguimiento de Envío"
                        description="Panel de seguimiento con estado del viaje"
                      />

                      <hr />

                      <h3>Preguntas Frecuentes</h3>

                      <div className="faq-item">
                        <h5>¿Cuánto tiempo tarda en procesarse mi solicitud?</h5>
                        <p>Generalmente las solicitudes se procesan en 24-48 horas hábiles. Para servicios urgentes, contacta directamente a tu representante.</p>
                      </div>

                      <div className="faq-item">
                        <h5>¿Puedo modificar una solicitud después de enviarla?</h5>
                        <p>Solo si el estado es "Nueva". Una vez en revisión, deberás contactar al equipo de operaciones.</p>
                      </div>

                      <div className="faq-item">
                        <h5>¿Cómo sé si mi viaje fue confirmado?</h5>
                        <p>Recibirás una notificación por correo electrónico cuando tu solicitud sea aprobada y el viaje sea programado.</p>
                      </div>

                      <div className="faq-item">
                        <h5>¿Puedo solicitar un viaje para el mismo día?</h5>
                        <p>Dependiendo de la disponibilidad. Para servicios urgentes, te recomendamos llamar directamente.</p>
                      </div>
                    </div>
                  </Tab>
                </Tabs>
              </Card.Body>
            </Card>
          </div>
        </Row>
      </div>
    </div>
  );
};

export default Manual;
